/**
 * Mocks for the ngrx store and datastore backend.
 * At the moment, these are still tightly coupled with the 'users' module
 * since we have no way to dynamically configure a new feature module
 * for testing purposes without changing the collections model. Ideally in
 * tests we could bootstrap something like a `DatastoreTestingModule`.
 *
 * TOOD: Break away from relying on the 'users' module.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Backend,
  CollectionActions,
  ErrorResponseData,
  mergeEntities,
  OrderByDirection,
  StoreFeatureState,
  SuccessResponseData,
  transformObjects,
} from '../';
import { BackendFetchTypes } from '../../backend.model';

const defaultJobs: ReadonlyArray<string> = ['js', 'html', 'css'];

@Injectable() // api-http.service
export class MockApiHttp {
  constructor(private http: HttpClient) {}

  fetch() {
    return this.http.get('');
  }
}

// Mock response from API must have `status` and `result` fields.
// The request is originally made in freelancer-http.service, which is the
// location where Angular's native HTTP methods are called.
export function getMockUserResponse(
  ids: ReadonlyArray<string | number>,
  name: string = 'mockUser',
  jobs: ReadonlyArray<string> = defaultJobs,
): SuccessResponseData<BackendFetchTypes['users']> {
  return {
    result: {
      users: ids.reduce(
        (acc, id) => ({
          ...acc,
          [id]: getMockUser(id, name, jobs),
        }),
        {},
      ),
    },
    status: 'success',
  };
}

export function getMockUser(
  id: string | number,
  name: string = 'mockUser',
  jobs: ReadonlyArray<string> = defaultJobs,
): MockUser {
  return {
    id: Number(id),
    username: name,
    displayName: name,
    jobs,
  };
}

export function mockBackend(): Backend<'users'> {
  return {
    defaultOrder: { field: 'id', direction: OrderByDirection.ASC },
    fetch: (_authUid, _userIds, _query) => ({
      endpoint: 'users/0.1/users',
    }),
  };
}

// User mocks
export interface MockUser {
  readonly id: number;
  readonly username: string;
  readonly displayName?: string;
  readonly jobs?: ReadonlyArray<string>;
}

// @ngrx Store mocks
function mockTransform(backendObj: any): any {
  return backendObj;
}

export function mockReducer(
  state: StoreFeatureState<'users'> = {},
  action: CollectionActions<'users'>,
) {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'users') {
        const { result, ref, order } = action.payload;
        if (result.users) {
          return mergeEntities<'users'>(
            state,
            transformObjects(result.users, mockTransform as any),
            order,
            ref,
          );
        }
        return state;
      }

      return state;
    }

    case 'WS_MESSAGE': {
      return state;
    }

    default:
      return state;
  }
}
